import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@services/api.service';
import { ERROR_MESSSAGES } from '@constants/common.constants';
import Error from '@components/Error';
import ServiceDownImg from '@assets/images/error/service-down.svg';
import NoInternetConnectionImg from '@assets/images/error/no-internet-connection.svg';

interface NetworkErrorProps {
	children: React.ReactNode;
}

enum ErrorType {
	NO_INTERNET = 'noInternet',
	SERVICE_DOWN = 'serviceDown',
}

const NetworkError = ({ children }: NetworkErrorProps) => {
	const [axiosError, setAxiosError] = useState<{ type: ErrorType; title: string; description: string } | null>(null);
	const [isOnline, setOnline] = useState(navigator.onLine);
	const fullUrl = window.location.href;

	useEffect(() => {
		const handleOnlineStatus = () => setOnline(navigator.onLine);
		window.addEventListener('online', handleOnlineStatus);
		window.addEventListener('offline', handleOnlineStatus);

		const responseInterceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			(error) => {
				const status = error.response?.status;
				const isNetworkError = !navigator.onLine;
				if (isNetworkError) {
					setAxiosError({
						type: ErrorType.NO_INTERNET,
						title: ERROR_MESSSAGES.NO_INTERNET_CONNECTION.TITLE,
						description: ERROR_MESSSAGES.NO_INTERNET_CONNECTION.DESCRIPTION,
					});
					setOnline(false);
				} else if ([500, 503, 504].includes(status) || error.code === 'ECONNABORTED') {
					setAxiosError({
						type: ErrorType.SERVICE_DOWN,
						title: ERROR_MESSSAGES.SERVICE_DOWN.TITLE,
						description: ERROR_MESSSAGES.SERVICE_DOWN.DESCRIPTION,
					});
				}
				return Promise.reject(error);
			},
		);

		return () => {
			window.removeEventListener('online', handleOnlineStatus);
			window.removeEventListener('offline', handleOnlineStatus);
			setAxiosError(null);
			axiosInstance.interceptors.response.eject(responseInterceptor);
		};
	}, [fullUrl]);

	if (!isOnline) {
		return (
			<Error
				img={<NoInternetConnectionImg width='100%' height='100%' />}
				title={ERROR_MESSSAGES.NO_INTERNET_CONNECTION.TITLE}
				description={ERROR_MESSSAGES.NO_INTERNET_CONNECTION.DESCRIPTION}
				btnText={ERROR_MESSSAGES.NO_INTERNET_CONNECTION.BTN_TEXT}
				btnCallback={() => window.location.reload()}
			/>
		);
	}

	return (
		<>
			{axiosError ? (
				<Error
					img={
						axiosError.type === ErrorType.NO_INTERNET ? (
							<NoInternetConnectionImg width='100%' height='100%' />
						) : (
							<ServiceDownImg width='100%' height='100%' />
						)
					}
					title={axiosError.title}
					description={
						<>
							<p>{axiosError.description}</p>
							{axiosError.type === ErrorType.SERVICE_DOWN && (
								<p className='mt-6 tablet:mt-0'>{ERROR_MESSSAGES.SERVICE_DOWN.SUB_DESCRIPTION}</p>
							)}
						</>
					}
					btnText={
						axiosError.type === ErrorType.NO_INTERNET
							? ERROR_MESSSAGES.NO_INTERNET_CONNECTION.BTN_TEXT
							: ERROR_MESSSAGES.SERVICE_DOWN.BTN_TEXT
					}
					btnCallback={
						axiosError.type === ErrorType.NO_INTERNET
							? () => location.replace(location.href.split('?')[0] + '?_=' + new Date().getTime())
							: () => window.location.reload()
					}
				/>
			) : (
				children
			)}
		</>
	);
};

export default NetworkError;
